import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useInfiniteQuery } from 'react-query';

import { getCleanedUrlParams, showError } from '@utils';
import { QUERY_KEYS } from 'constants/queryKeys';
import { driversServices } from '@services';
import { HANDOVER_CARS_TYPE } from '@constants';
import { getHandoverCarsPayload, handleHandoveredCars } from './utils';

export const HANDOVER_CARS_PAGE_SIZE = 100;

export const useHandoverCarsOperations = (handoverType) => {
  const router = useRouter();

  const query = router.query?.query || '';
  const location = router.query?.location;
  const type = router.query?.type;
  const dateFrom = router.query?.dateFrom;
  const dateTo = router.query?.dateTo;
  const orderBy =
    router.query?.orderBy ||
    `${
      handoverType === HANDOVER_CARS_TYPE.DELIVERY ? 'delivery' : 'return'
    }Date`;
  const assignee = router.query?.assignee;
  const order = router.query?.order || 'desc';

  const cleanedParams = getCleanedUrlParams({
    page: 1,
    pageSize: HANDOVER_CARS_PAGE_SIZE,
    order,
    orderBy,
    location,
    type,
    searchFor: handoverType,
    query,
    dateFrom,
    dateTo,
    assignee,
  });

  const fetchVehiclesPayload = getHandoverCarsPayload(cleanedParams);

  const fetchVehicles = async ({ pageParam = 1 }) => {
    const params = {
      ...fetchVehiclesPayload,
      page: pageParam,
    };

    const res = await driversServices.getHandoveredCars(params);
    const pagesNumber =
      res?.meta?.total < HANDOVER_CARS_PAGE_SIZE
        ? 1
        : Math.ceil((res?.meta?.total || 1) / HANDOVER_CARS_PAGE_SIZE);

    return {
      vehicles: res?.data || [],
      hasNextPage: pageParam < pagesNumber,
    };
  };
  const handoverCarResponse = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_HANDOVER_CARS_KEY, fetchVehiclesPayload],
    queryFn: fetchVehicles,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasNextPage) {
        return allPages.length + 1;
      }
      return undefined;
    },
    enabled: router.isReady,
  });
  const vehicles =
    handoverCarResponse?.data?.pages.flatMap((p) => p.vehicles) || [];
  const groups = handleHandoveredCars(
    vehicles,
    handoverType === HANDOVER_CARS_TYPE.DELIVERY ? 'deliveryDate' : 'returnDate'
  );

  useEffect(() => {
    if (handoverCarResponse?.error) {
      showError(handoverCarResponse.error);
    }
  }, [handoverCarResponse?.error]);

  return {
    total: handoverCarResponse?.data?.meta?.total,
    loading:
      handoverCarResponse.isFetching || handoverCarResponse.isFetchingNextPage,
    groups,
    fetchNextPage: handoverCarResponse?.fetchNextPage,
    hasNextPage: handoverCarResponse?.hasNextPage,
  };
};
